import { memo } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Header,
  Link,
  RadioButton,
  SpaceBetween,
  Spinner,
  Table,
} from '@amzn/awsui-components-core';
import { DateTime } from 'luxon';

import DiffView from '../workflow-viewer/DiffView';
import { HistoryRow, useHistoryTabViewModel } from './HistoryTabViewModel';

export const HistoryTabView = memo(function HistoryTabView() {
  const vm = useHistoryTabViewModel();

  if (!vm) return null;

  if (vm.isLoading) {
    return (
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <Spinner size="normal" />
        <Box variant="p" color="text-status-inactive">
          Loading publication history…
        </Box>
      </SpaceBetween>
    );
  }

  if (vm.loadError) {
    return (
      <Alert type="error" action={<Button onClick={vm.retry}>Retry</Button>}>
        {vm.loadError}
      </Alert>
    );
  }

  if (vm.isEmpty) {
    return (
      <Container header={<Header variant="h3">Publication history</Header>}>
        <Box textAlign="center" color="text-body-secondary" padding={{ vertical: 'xxl' }}>
          No publication history yet.
        </Box>
      </Container>
    );
  }

  return (
    <SpaceBetween size="l">
      <Container
        header={
          <Header variant="h3" counter={`(${vm.rows.length})`}>
            Publication history
          </Header>
        }
      >
        <SpaceBetween size="m">
          <Table<HistoryRow>
            columnDefinitions={[
              {
                id: 'versionA',
                header: 'A',
                width: 50,
                minWidth: 50,
                cell: (row) => (
                  <RadioButton
                    checked={vm.versionA === row.workflowId}
                    disabled={!row.isComparable}
                    name="history-version-a"
                    onSelect={() => vm.selectVersionA(row.workflowId)}
                  />
                ),
              },
              {
                id: 'versionB',
                header: 'B',
                width: 50,
                minWidth: 50,
                cell: (row) => (
                  <RadioButton
                    checked={vm.versionB === row.workflowId}
                    disabled={!row.isComparable}
                    name="history-version-b"
                    onSelect={() => vm.selectVersionB(row.workflowId)}
                  />
                ),
              },
              {
                id: 'version',
                header: 'Version',
                cell: (row) => `${row.workflowId}.0`,
              },
              {
                id: 'author',
                header: 'Author',
                cell: (row) => row.authorName,
              },
              {
                id: 'date',
                header: 'Date',
                cell: (row) => row.publicationDate.toLocaleString(DateTime.DATE_FULL),
              },
              {
                id: 'status',
                header: 'Status',
                cell: (row) => row.status,
              },
              {
                id: 'view',
                header: '',
                width: 70,
                minWidth: 70,
                cell: (row) =>
                  row.viewHref === undefined ? (
                    <Box color="text-status-inactive">N/A</Box>
                  ) : (
                    <Link href={row.viewHref} external variant="secondary">
                      View
                    </Link>
                  ),
              },
            ]}
            items={vm.rows as HistoryRow[]}
            variant="embedded"
            trackBy="workflowId"
          />

          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Box variant="p" color="text-status-inactive">
              Select two versions to compare.
            </Box>
            <Button onClick={vm.compare} disabled={!vm.isCompareEnabled} loading={vm.isComparing}>
              Compare Versions
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      {vm.compareError && (
        <Alert type="error" action={<Button onClick={vm.compare}>Retry</Button>}>
          {vm.compareError}
        </Alert>
      )}

      {vm.diffData && (
        <DiffView
          oldValue={vm.diffData.oldValue}
          newValue={vm.diffData.newValue}
          leftTitle={vm.diffData.leftTitle}
          rightTitle={vm.diffData.rightTitle}
        />
      )}
    </SpaceBetween>
  );
});
